import React from "react";
import {
  Document, Page, View, Text, Image, StyleSheet, Font,
} from "@react-pdf/renderer";

// ─── Estilos del documento ────────────────────────────────────────────────────
const BRAND = "#9f0059";
const DARK  = "#1a1a1a";
const GREY  = "#555555";
const LIGHT = "#f5f0f3";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    color: DARK,
    paddingBottom: 48,
  },

  // ── Header ──
  headerBand: {
    backgroundColor: "#d0ced0",
    padding: "8 24 8 24",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 0,
  },
  headerLogoLeft: {
    width: 80,
    height: 80,
    objectFit: "contain",
  },
  headerCenter: {
    flex: 1,
    alignItems: "center",
  },
  headerTitle: {
    color: "#1a1a1a",
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 3,
  },
  headerSub: {
    color: "#555555",
    fontSize: 9,
    marginTop: 3,
  },
  headerLogoRight: {
    width: 80,
    height: 80,
    objectFit: "contain",
  },
  headerLogoPlaceholder: {
    width: 80,
  },

  // ── Cuerpo ──
  body: {
    paddingHorizontal: 32,
    paddingTop: 20,
  },

  // ── Destinatario + fecha ──
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  metaBlock: {
    flexDirection: "column",
    gap: 3,
  },
  metaLabel: {
    fontSize: 8,
    color: GREY,
    textTransform: "uppercase",
    letterSpacing: 1,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 11,
    color: DARK,
    fontFamily: "Helvetica-Bold",
  },

  // ── Presentación ──
  presentacionBox: {
    backgroundColor: LIGHT,
    borderRadius: 4,
    padding: "10 14",
    marginBottom: 18,
    borderLeft: `3 solid ${BRAND}`,
  },
  presentacionText: {
    fontSize: 9.5,
    color: GREY,
    fontFamily: "Helvetica-Oblique",
    lineHeight: 1.5,
  },

  // ── Tabla de productos ──
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#2d2d2d",
    padding: "7 8",
    marginBottom: 0,
  },
  tableHeaderText: {
    color: "#ffffff",
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  tableRow: (even) => ({
    flexDirection: "row",
    backgroundColor: even ? "#fafafa" : "#ffffff",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottom: "1 solid #ede8ed",
  }),
  tableRowText: {
    fontSize: 9.5,
    color: DARK,
  },
  tableRowTextRight: {
    fontSize: 9.5,
    color: DARK,
    textAlign: "right",
  },
  // Columnas: descripcion flex:4, cantidad flex:1, precio flex:1.5
  colDesc:   { flex: 4 },
  colCant:   { flex: 1,   textAlign: "center" },
  colPrecio: { flex: 1.5, textAlign: "right" },

  // ── Condiciones ──
  seccionTitle: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: GREY,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    marginBottom: 8,
    borderBottom: `1 solid #ede8ed`,
    paddingBottom: 4,
  },
  condicionItem: {
    flexDirection: "row",
    marginBottom: 4,
    paddingLeft: 4,
  },
  bullet: {
    color: BRAND,
    fontSize: 10,
    marginRight: 6,
    lineHeight: 1.4,
  },
  condicionText: {
    flex: 1,
    fontSize: 9.5,
    color: GREY,
    lineHeight: 1.4,
  },

  // ── Firma ──
  firmaSection: {
    marginTop: 24,
    alignItems: "flex-end",
  },
  firmaSaludo: {
    fontSize: 9,
    color: GREY,
    marginBottom: 4,
  },
  firmaNombre: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: DARK,
    borderTop: `1 solid #ccc`,
    paddingTop: 6,
    minWidth: 160,
    textAlign: "center",
  },
  firmaCargo: {
    fontSize: 8.5,
    color: GREY,
    textAlign: "center",
    marginTop: 2,
    minWidth: 160,
  },

  // ── Footer ──
  footer: {
    position: "absolute",
    bottom: 16,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `1 solid #ede8ed`,
    paddingTop: 6,
  },
  footerText: {
    fontSize: 8,
    color: "#aaa",
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMoney = (n) => {
  const num = parseFloat(n) || 0;
  return "$" + num.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtFecha = (str) => {
  if (!str) return "";
  const [y, m, d] = str.split("-");
  return `${d}/${m}/${y}`;
};

// ─── Documento PDF ────────────────────────────────────────────────────────────
const PresupuestoPDF = ({
  orderId,
  fecha,
  destinatario,
  textoPresentacion,
  condiciones,
  nombreUsuario,
  cargoUsuario,
  monto,
  items = [],
  cliente,
  logoTwinpackUrl,
  logoProveedorUrl,
}) => {
  const condArr = (condiciones || "").split("\n").filter((c) => c.trim());

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* ── Header ── */}
        <View style={styles.headerBand} fixed>
          {/* Logo Twinpack */}
          {logoTwinpackUrl ? (
            <Image src={logoTwinpackUrl} style={styles.headerLogoLeft} />
          ) : (
            <View style={styles.headerLogoPlaceholder} />
          )}

          {/* Título central */}
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle}>COTIZACIÓN</Text>
            <Text style={styles.headerSub}>
              Nº {orderId}  ·  {fmtFecha(fecha)}
            </Text>
          </View>

          {/* Logo proveedor */}
          {logoProveedorUrl ? (
            <Image src={logoProveedorUrl} style={styles.headerLogoRight} />
          ) : (
            <View style={styles.headerLogoPlaceholder} />
          )}
        </View>

        {/* ── Cuerpo ── */}
        <View style={styles.body}>

          {/* Destinatario + fecha */}
          <View style={styles.metaRow}>
            <View style={styles.metaBlock}>
              <Text style={styles.metaLabel}>Para</Text>
              <Text style={styles.metaValue}>{destinatario || "—"}</Text>
              {cliente ? <Text style={{ fontSize: 9, color: GREY, marginTop: 2 }}>{cliente}</Text> : null}
            </View>
            <View style={{ ...styles.metaBlock, alignItems: "flex-end" }}>
              <Text style={styles.metaLabel}>Fecha</Text>
              <Text style={styles.metaValue}>{fmtFecha(fecha)}</Text>
            </View>
          </View>

          {/* Texto de presentación */}
          {textoPresentacion ? (
            <View style={styles.presentacionBox}>
              <Text style={styles.presentacionText}>{textoPresentacion}</Text>
            </View>
          ) : null}

          {/* Tabla de productos */}
          {items.length > 0 && (
            <View style={{ marginBottom: 4 }}>
              {/* Header */}
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderText, styles.colDesc]}>Descripción</Text>
                <Text style={[styles.tableHeaderText, styles.colCant, { textAlign: "center" }]}>Cant.</Text>
                <Text style={[styles.tableHeaderText, styles.colPrecio, { textAlign: "right" }]}>P. Unit.</Text>
              </View>
              {/* Filas */}
              {items.map((it, idx) => (
                <View key={idx} style={styles.tableRow(idx % 2 === 0)}>
                  <Text style={[styles.tableRowText, styles.colDesc]}>{it.descripcion || "—"}</Text>
                  <Text style={[styles.tableRowText, styles.colCant, { textAlign: "center" }]}>
                    {it.cantidad}
                  </Text>
                  <Text style={[styles.tableRowText, styles.colPrecio]}>
                    {fmtMoney(it.precio)}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Condiciones comerciales */}
          {condArr.length > 0 && (
            <View style={{ marginTop: 20, marginBottom: 20 }}>
              <Text style={styles.seccionTitle}>Condiciones comerciales</Text>
              {condArr.map((c, i) => (
                <View key={i} style={styles.condicionItem}>
                  <Text style={styles.bullet}>•</Text>
                  <Text style={styles.condicionText}>{c.trim()}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Firma */}
          <View style={styles.firmaSection}>
            <Text style={styles.firmaSaludo}>Saluda atentamente,</Text>
            <Text style={styles.firmaNombre}>{nombreUsuario || "—"}</Text>
            {cargoUsuario ? (
              <Text style={styles.firmaCargo}>{cargoUsuario}</Text>
            ) : null}
          </View>
        </View>

        {/* ── Footer fijo ── */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Twinpack — Cotización Nº {orderId}</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) =>
            `Página ${pageNumber} de ${totalPages}`
          } />
        </View>

      </Page>
    </Document>
  );
};

export default PresupuestoPDF;
